'use client';

import { useCallback } from 'react';

/**
 * Custom hook to interact with the Intercom API
 * Provides type-safe methods for common Intercom operations
 *
 * @returns Object with Intercom API methods
 *
 * @example
 * ```tsx
 * const { show, hide, update, showMessages } = useIntercom();
 *
 * // Show the Intercom messenger
 * show();
 *
 * // Update user data
 * update({ customAttribute: 'value' });
 * ```
 */
export const useIntercom = () => {
  /**
   * Show the Intercom Messenger
   */
  const show = useCallback(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('show');
    }
  }, []);

  /**
   * Hide the Intercom Messenger
   */
  const hide = useCallback(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('hide');
    }
  }, []);

  /**
   * Show the message list
   */
  const showMessages = useCallback(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('showMessages');
    }
  }, []);

  /**
   * Show a new message
   * @param message - Optional pre-populated message text
   */
  const showNewMessage = useCallback((message?: string) => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('showNewMessage', message);
    }
  }, []);

  /**
   * Update Intercom with new user data or trigger a refresh
   * @param data - Optional user data to update
   */
  const update = useCallback((data?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('update', data);
    }
  }, []);

  /**
   * Track a custom event in Intercom
   * @param eventName - Name of the event to track
   * @param metadata - Optional metadata about the event
   */
  const trackEvent = useCallback((eventName: string, metadata?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('trackEvent', eventName, metadata);
    }
  }, []);

  /**
   * Get the current visitor ID
   * @returns The visitor ID if available
   */
  const getVisitorId = useCallback((): string | undefined => {
    if (typeof window !== 'undefined' && window.Intercom) {
      return window.Intercom('getVisitorId');
    }
    return undefined;
  }, []);

  /**
   * Start a new tour
   * @param tourId - The ID of the tour to start
   */
  const startTour = useCallback((tourId: number) => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('startTour', tourId);
    }
  }, []);

  return {
    show,
    hide,
    showMessages,
    showNewMessage,
    update,
    trackEvent,
    getVisitorId,
    startTour,
  };
};
